import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import CustomButton from "../../../components/form/CustomButton";
import CustomInput from "../../../components/form/CustomInput";
import GoogleMapsStops from "../../../components/maps/GoogleMapsStops";
import StepTowForm from "./StepTwoForm";
import StopsList from "../StopsList";
import { useDispatch, useSelector } from "react-redux";
import { reset_stop, update_load } from "../../../store/loads/action";
import { ToastHelper } from "../../../helpers/configRoutes/alerts/alertHelper";
import {
    EMPTY_MILES_STATUS_ID,
    isEmptyMilesStop,
    normalizeStopsOrder,
    recalculateStopsMiles,
} from "../stopMiles";

const PREVIOUS_STOP_DATE_ERROR =
    "Pickup must be after the previous stop delivery date and time";

const StepTow = ({
    onNext,
    onBack,
    onCancel,
}) => {
    const dispatch = useDispatch();
    const { stop, validationsStop, load } = useSelector((state) => state.Load);

    const [newStop, setNewStop] = useState(false);
    const [editingStop, setEditingStop] = useState(null);
    const [stops, setStops] = useState(load.stops || []);
    const [stopsDeleted, setStopsDeleted] = useState(load.stops_deleted || []);
    const [emptyMilesPromptShown, setEmptyMilesPromptShown] = useState(false);

    const baseStopValues = {
        id: "",
        name: "",
        order: "",
        status: {},
        status_id: "",
        trailer_id: load.trailer_id || "",
        mile: "",
        lat: 37.7749,
        lng: -122.4194,
        date: "",
        load_time: "",
        delivery_date: "",
        delivery_time: "",
        geofence: 1,
    };

    const [manualMilesEnabled, setManualMilesEnabled] = useState(false);
    const [manualMiles, setManualMiles] = useState("");

    const buildDateTime = (dateVal, timeStr) => {
        if (!dateVal || !timeStr) return null;

        const newDate = new Date(dateVal);
        const timeLower = String(timeStr).toLowerCase();
        const isPM = timeLower.includes("p. m.") || timeLower.includes("pm");
        const isAM = timeLower.includes("a. m.") || timeLower.includes("am");
        const cleanTime = String(timeStr).replace(/[^0-9:]/g, "");
        let [hours, minutes] = cleanTime.split(":").map(Number);

        if (Number.isNaN(hours) || Number.isNaN(minutes)) {
            return null;
        }

        if (isPM && hours < 12) hours += 12;
        if (isAM && hours === 12) hours = 0;

        newDate.setHours(hours);
        newDate.setMinutes(minutes);
        newDate.setSeconds(0);
        newDate.setMilliseconds(0);

        return newDate;
    };

    const matchStop = (left, right) => {
        if (!left || !right) {
            return false;
        }

        if (left.id && right.id) {
            return String(left.id) === String(right.id);
        }

        return Number(left.order) === Number(right.order);
    };

    const getPreviousStopError = (currentStop, currentStops) => {
        const currentOrder = Number(currentStop?.order) || currentStops.length + 1;
        const previousStop = currentStops.find(
            (item) => Number(item.order) === currentOrder - 1
        );

        if (!previousStop) return null;

        const previousDeliveryDateTime = buildDateTime(
            previousStop.delivery_date,
            previousStop.delivery_time
        );
        const currentPickupDateTime = buildDateTime(
            currentStop.date,
            currentStop.load_time
        );

        if (!previousDeliveryDateTime || !currentPickupDateTime) {
            return null;
        }

        if (currentPickupDateTime.getTime() <= previousDeliveryDateTime.getTime()) {
            return PREVIOUS_STOP_DATE_ERROR;
        }

        return null;
    };

    const getLastLoadCandidates = () => {
        const rawCandidates = [
            load.truck_last_load
                ? {
                    ...load.truck_last_load,
                    source: "truck",
                    label: "Truck",
                }
                : null,
            load.trailer_last_load
                ? {
                    ...load.trailer_last_load,
                    source: "trailer",
                    label: "Trailer",
                }
                : null,
        ].filter(Boolean);

        const uniqueCandidates = rawCandidates.filter((candidate, index, array) => {
            const key = `${candidate.load_id}-${candidate.last_stop?.id || "no-stop"}`;
            return (
                array.findIndex(
                    (item) => `${item.load_id}-${item.last_stop?.id || "no-stop"}` === key
                ) === index
            );
        });

        return uniqueCandidates.sort((a, b) => {
            const aDate = buildDateTime(
                a.last_stop?.delivery_date || a.last_stop?.date,
                a.last_stop?.delivery_time || a.last_stop?.load_time
            )?.getTime() || 0;
            const bDate = buildDateTime(
                b.last_stop?.delivery_date || b.last_stop?.date,
                b.last_stop?.delivery_time || b.last_stop?.load_time
            )?.getTime() || 0;
            return bDate - aDate;
        });
    };

    const buildEmptyMilesStop = (candidate) => {
        const lastStop = candidate.last_stop || {};
        const dateValue = lastStop.delivery_date || lastStop.date || "";
        const timeValue = lastStop.delivery_time || lastStop.load_time || "";

        return {
            id: `empty-miles-${candidate.load_id || Date.now()}`,
            name: lastStop.name,
            order: 1,
            status_id: EMPTY_MILES_STATUS_ID,
            trailer_id: load.trailer_id || "",
            status: {
                id: EMPTY_MILES_STATUS_ID,
                name: "Vacío",
            },
            mile: 0,
            lat: lastStop.lat,
            lng: lastStop.lng,
            date: dateValue,
            load_time: timeValue,
            delivery_date: dateValue,
            delivery_time: timeValue,
            geofence: lastStop.geofence || 1,
            generated_empty_miles: true,
            generated_from_equipment: candidate.source,
            previous_load_id: candidate.load_id,
        };
    };

    const validation = useFormik({
        initialValues: stop,
        enableReinitialize: false,
        validationSchema: Yup.object(validationsStop),
        onSubmit: async (values, { setFieldError }) => {
            const previousStopError = getPreviousStopError(values, stops);

            if (previousStopError) {
                setFieldError("date", previousStopError);
                return;
            }

            const nextStop = {
                ...values,
                order: editingStop ? editingStop.order : stops.length + 1,
            };

            const nextStops = editingStop
                ? normalizeStopsOrder(
                    stops.map((currentStop) =>
                        matchStop(currentStop, editingStop)
                            ? { ...currentStop, ...nextStop, order: currentStop.order }
                            : currentStop
                    )
                )
                : normalizeStopsOrder([...stops, nextStop]);

            const recalculatedStops = await recalculateStopsMiles(nextStops);

            setStops(recalculatedStops);
            setEditingStop(null);
            dispatch(reset_stop());
            setNewStop(false);
            validation.resetForm();
        },
    });

    const { values } = validation;

    useEffect(() => {
        if (!newStop) {
            return;
        }

        if (editingStop) {
            validation.setValues(
                {
                    ...baseStopValues,
                    ...editingStop,
                },
                false
            );
            return;
        }

        validation.setValues(
            {
                ...baseStopValues,
                order: stops.length + 1,
                trailer_id: load.trailer_id || "",
            },
            false
        );
    }, [newStop, editingStop]); // eslint-disable-line react-hooks/exhaustive-deps

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!newStop) return;

        const previousStopError = getPreviousStopError(values, stops);

        if (previousStopError) {
            validation.setFieldError("date", previousStopError);
            return;
        }

        if (validation.errors.date === PREVIOUS_STOP_DATE_ERROR) {
            validation.setFieldError("date", undefined);
        }
    }, [newStop, stops, values.date, values.load_time]); // eslint-disable-line react-hooks/exhaustive-deps

    const autoPracticalMiles = stops
        .reduce((acc, item) => acc + (parseFloat(item.mile || 0) || 0), 0)
        .toFixed(2);

    const deleteItem = async (list, item) => {
        if (list !== "stops") return;

        const nextStops = normalizeStopsOrder(
            stops.filter((currentStop) => {
                if (item.id && currentStop.id) {
                    return String(currentStop.id) !== String(item.id);
                }

                return Number(currentStop.order) !== Number(item.order);
            })
        );
        const recalculatedStops = await recalculateStopsMiles(nextStops);

        if (item.id && !String(item.id).startsWith("empty-miles-")) {
            setStopsDeleted((prev) => [...prev, item.id]);
        }

        if (editingStop && matchStop(editingStop, item)) {
            setEditingStop(null);
            setNewStop(false);
            validation.resetForm();
            dispatch(reset_stop());
        }

        setStops(recalculatedStops);
    };

    const openAddStop = () => {
        setEditingStop(null);
        setNewStop(true);
    };

    const handleEditStop = (item) => {
        setEditingStop(item);
        setNewStop(true);
    };

    const handleCancelStopForm = () => {
        setEditingStop(null);
    };

    const handleReorderStops = async (fromIndex, toIndex) => {
        const nextStops = [...stops];
        const [movedStop] = nextStops.splice(fromIndex, 1);
        nextStops.splice(toIndex, 0, movedStop);
        const reorderedStops = normalizeStopsOrder(nextStops);
        const recalculatedStops = await recalculateStopsMiles(reorderedStops);
        setStops(recalculatedStops);
    };

    const saveStops = () => {
        if (stops.length === 0) {
            ToastHelper(
                "You must add at least one stop before saving this load.",
                "error"
            );
            return false;
        }

        const practicalMiles = manualMilesEnabled
            ? (parseFloat(manualMiles || 0) || 0).toFixed(2)
            : autoPracticalMiles;

        dispatch(
            update_load({
                ...load,
                stops,
                stops_deleted: stopsDeleted,
                practical_miles: practicalMiles,
            })
        );

        onNext();
    };

    const displayedMiles = manualMilesEnabled ? manualMiles : autoPracticalMiles;
    const emptyMilesCandidate = getLastLoadCandidates().find(
        (item) => item?.last_stop?.name && item?.last_stop?.lat && item?.last_stop?.lng
    );

    useEffect(() => {
        if (
            emptyMilesPromptShown ||
            newStop ||
            editingStop ||
            !emptyMilesCandidate ||
            stops.some(isEmptyMilesStop)
        ) {
            return;
        }

        setEmptyMilesPromptShown(true);

        Swal.fire({
            title: "Add empty miles?",
            text: `${emptyMilesCandidate.label} ${emptyMilesCandidate.equipment_number || ""} last finished at ${emptyMilesCandidate.last_stop.name}. Do you want to add that point as an empty miles stop before this load? Miles will stay at 0 until the next stop calculates the distance automatically.`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#0D6EFD",
            cancelButtonColor: "#6C757D",
            confirmButtonText: "Yes, add empty miles stop",
            cancelButtonText: "No, continue without it",
            reverseButtons: true,
        }).then(async (result) => {
            if (!result.isConfirmed) {
                return;
            }

            const nextStops = normalizeStopsOrder([
                buildEmptyMilesStop(emptyMilesCandidate),
                ...stops,
            ]);
            const recalculatedStops = await recalculateStopsMiles(nextStops);
            setStops(recalculatedStops);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emptyMilesPromptShown, emptyMilesCandidate, newStop, editingStop, stops]);

    const handleToggleManualMiles = () => {
        setManualMilesEnabled((prev) => !prev);

        if (!manualMilesEnabled) {
            setManualMiles(autoPracticalMiles);
        }
    };

    return (
        <>
            <div className="space-y-4">
                <h2 className="text-lg font-semibold text-cs-dark">Stops & route</h2>
                <p className="text-sm text-cs-muted">
                    Add pickup, delivery and intermediate stops, including addresses and schedule.
                </p>

                <div className="space-y-3">
                    <div className="grid grid-cols-1 gap-4 p-2">
                        <GoogleMapsStops
                            stops={stops}
                            setStops={setStops}
                            validation={validation}
                            newStop={newStop}
                        />
                    </div>

                    {newStop ? (
                        <StepTowForm
                            validation={validation}
                            setNewStop={setNewStop}
                            loadTrailerId={load.trailer_id}
                            isEditing={!!editingStop}
                            onCancel={handleCancelStopForm}
                            emptyMilesStatusId={EMPTY_MILES_STATUS_ID}
                        />
                    ) : (
                        <div
                            className="
                w-full
                bg-cs-bg-alt
                border border-cs-border
                rounded-xl
                px-3 py-2
              "
                        >
                            <StopsList
                                setNewStop={openAddStop}
                                items={stops || []}
                                deleteItem={deleteItem}
                                onEditStop={handleEditStop}
                                onReorderStops={handleReorderStops}
                            />
                        </div>
                    )}

                    <div className="mt-3 max-w-md space-y-2">
                        <div className="flex items-center justify-between gap-3">
                            <span className="text-xs font-medium text-cs-dark">
                                Practical miles
                            </span>

                            <button
                                type="button"
                                onClick={handleToggleManualMiles}
                                className={`
                  inline-flex items-center gap-1 px-2 py-1 rounded-full
                  border text-[11px]
                  ${manualMilesEnabled
                                        ? "bg-cs-primary text-white border-cs-primary"
                                        : "bg-cs-bg text-cs-muted border-cs-border"
                                    }
                `}
                            >
                                <span className="material-symbols-outlined text-[14px]">
                                    {manualMilesEnabled ? "edit" : "calculate"}
                                </span>
                                {manualMilesEnabled ? "Manual input" : "Auto calculated"}
                            </button>
                        </div>

                        <CustomInput
                            label=""
                            type="text"
                            name="practical_miles"
                            labelClassName="hidden"
                            error={undefined}
                            value={displayedMiles}
                            handleChange={
                                manualMilesEnabled
                                    ? (event) => setManualMiles(event.target.value)
                                    : () => {}
                            }
                            disabled={!manualMilesEnabled}
                        />

                        <p className="mt-1 text-[11px] text-cs-muted">
                            {manualMilesEnabled ? (
                                <>
                                    You are overriding the automatic miles. Current auto sum:{" "}
                                    <span className="font-semibold">{autoPracticalMiles}</span> mi.
                                </>
                            ) : (
                                <>
                                    Sum of miles between all stops in this load:{" "}
                                    <span className="font-semibold">{autoPracticalMiles}</span> mi.
                                </>
                            )}
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-6 pt-4 border-t border-cs-border">
                <div className="text-xs text-cs-muted">
                    Step 2 of 3 – Configure stops and route.
                </div>
                <div className="flex justify-end gap-3">
                    <CustomButton
                        type="button"
                        onClick={onCancel}
                        className="
              inline-flex items-center gap-2
              px-4 py-2.5
              rounded-xl
              bg-cs-danger hover:bg-cs-primary-hover
              text-white text-sm font-medium
              shadow-md hover:shadow-lg
              transition-all
            "
                    >
                        Cancel
                    </CustomButton>

                    <CustomButton
                        type="button"
                        onClick={onBack}
                        className="
              inline-flex items-center gap-2
              px-4 py-2.5
              rounded-xl
              bg-cs-bg
              border border-cs-border
              text-sm text-cs-dark
              hover:bg-cs-bg-alt
              transition-all
            "
                    >
                        <span className="material-symbols-outlined text-sm">
                            arrow_back
                        </span>
                        Back
                    </CustomButton>

                    <CustomButton
                        type="button"
                        onClick={saveStops}
                        className="
              inline-flex items-center gap-2
              px-4 py-2.5
              rounded-xl
              bg-cs-primary hover:bg-cs-primary-hover
              text-white text-sm font-medium
              shadow-md hover:shadow-lg
              transition-all
            "
                    >
                        Next step
                        <span className="material-symbols-outlined text-sm">
                            arrow_forward
                        </span>
                    </CustomButton>
                </div>
            </div>
        </>
    );
};

export default StepTow;
